import { Nullable } from "../../../../../../../common/utils/nullable";
import { SaveReviewTasks, SubmitReviewTasks, UpdatedTask } from "./UpdateReviewTasks";
import { isEmptyString, isNullOrUndefined } from "../../../../../../../common/utils";
import { TooltipMessage } from "../../../../../../../common/data/TooltipMsg";
import { FilterKeys, Filters } from "../controller";
import { difference } from "lodash";

enum DecisionStatus {
  Draft = "DRAFT",
  Submit = "SUBMITTED"
}

type ReviewTasksGroup = {
  rowSpan: number
  reviewTaskIds: Array<String>
  shouldBeSkipped: boolean
}

export type Pagination = {
  pageSize: number
}

type UserWithName = {
  id: string
  name: string
}

type DecisionReason = {
  id: number
}

type ReviewTaskTicket = {
  id: Nullable<string>
  status: Nullable<string>
  url: Nullable<string>
}

type ReAssignment = {
  candidates: Array<UserWithName>;
  isReAssignable: boolean;
  hasNoDelegations: boolean;
  tooltipMessage: string;
};

export class ReviewTask {
  static mandatoryAttributes = [
    "key",
    "reviewTaskId",
    "reviewId",
    "revieweeId",
    "delegatedTo",
    "hasEditAccess",
    "decision",
    "reviewerComment",
    "reviewedBy",
    "reviewedAt",
    "isMyTask",
    "reassignment",
  ];

  key: string;
  reviewTaskId: string;
  reviewId: string;
  revieweeId: string;
  delegatedTo: string;
  hasEditAccess: boolean;
  decision: string;
  reviewerComment: string;
  reviewedBy: string;
  reviewedAt: string;
  isMyTask: boolean;
  isAssignedToCurrentUser: boolean;
  decisionReason: Nullable<DecisionReason>;
  reviewTaskTicket: Nullable<ReviewTaskTicket>;
  status: Nullable<DecisionStatus>;
  verificationStatus: Nullable<string>;
  isCommentRequired: boolean = false;
  isActionRequired: boolean = false;
  originalDecisions: { reviewerComment: string; decision: string, decisionReason: Nullable<DecisionReason> };
  isIgnoredFromSubmission: boolean = false;
  reassignment: ReAssignment;

  [otherAttributes: string]: any

  constructor(input: Object) {
    for (const [key, value] of Object.entries(input)) {
      this[key] = value;
    }
    const inputKeys = Array.from(Object.keys(input));
    const missedMandatoryFields = ReviewTask.mandatoryAttributes.filter((mandatoryAttribute) => !inputKeys.includes(mandatoryAttribute));
    if (missedMandatoryFields.length > 0) {
      console.log(missedMandatoryFields.join(", "));
      throw new Error(`Following fields are not present on creating Review Task object ${missedMandatoryFields.join(", ")}`);
    }
    this.originalDecisions = {
      reviewerComment: this.reviewerComment,
      decision: this.decision,
      decisionReason: this.decisionReason,
    };
  }

  markTaskIfEdited(
    editedReviewTasks: Array<{
      action: string,
      comment: string,
      reviewTaskId: string,
      reviewedBy: string,
      reviewedAt: string,
      decisionReason: Nullable<DecisionReason>
    }>,
    isCommentRequired: Function,
    isActionRequiredWhenCommentIsFilled: Function,
  ) {
    if (this.hasEditAccess) {
      const editedReviewTask = editedReviewTasks.find((editedTask) => editedTask.reviewTaskId === this.reviewTaskId);
      if (editedReviewTask) {
        const { action, comment, decisionReason } = editedReviewTask;
        this.decision = action ?? "";
        this.reviewerComment = comment ?? "";
        this.decisionReason = decisionReason ?? null;
        this.isCommentRequired = isCommentRequired(action, comment, decisionReason);
        this.isActionRequired = isActionRequiredWhenCommentIsFilled(action, comment, decisionReason);
        this.reviewedBy = editedReviewTask.reviewedBy;
        this.reviewedAt = editedReviewTask.reviewedAt;
      }
    }
  }

  reset() {
    if (this.status === DecisionStatus.Draft) {
      this.decision = this.originalDecisions.decision;
      this.reviewerComment = this.originalDecisions.reviewerComment;
      this.decisionReason = this.originalDecisions.decisionReason;
      this.isCommentRequired = false;
      this.isActionRequired = false;
    } else {
      this.resetComment();
    }
  }

  resetComment() {
    this.reviewerComment = "";
    this.decisionReason = null;
  }

  isValidDecision(): boolean {
    return !(this.isCommentRequired || this.isActionRequired) && !isEmptyString(this.decision);
  }

  updateTaskPayload(): UpdatedTask {
    return {
      id: this.reviewTaskId,
      decision: this.decision,
      comment: this.reviewerComment,
      decisionReason: this.decisionReason,

    };
  }

  canBeSubmitted(): boolean {
    const local = this.isCurrentDecisionDifferentFromOriginal();
    const cond = this.hasEditAccess && this.isValidDecision() && (this.status === DecisionStatus.Draft || local) && !this.isIgnoredFromSubmission;
    return cond;
  }

  canBeSaved(): boolean {
    return this.isValidDecision() && this.isCurrentDecisionDifferentFromOriginal() && this.hasEditAccess;
  }

  isCurrentDecisionDifferentFromOriginal(): boolean {
    return this.decision !== this.originalDecisions.decision
      || this.reviewerComment !== this.originalDecisions.reviewerComment
      || this.decisionReason?.id !== this.originalDecisions.decisionReason?.id;
  }

  isSaved(): boolean {
    return this.status === DecisionStatus.Draft;
  }

  isSubmitted(): boolean {
    return this.status === DecisionStatus.Submit;
  }

  isPendingDecision(): boolean {
    return this.status === null;
  }

  markTaskAsIgnored() {
    this.isIgnoredFromSubmission = true;
  }

  containsKey(key: string): boolean {
    return this.key === key;
  }

  resetIgnoredFlagToFalse() {
    this.isIgnoredFromSubmission = false;
  }

  isReasonNotRequired(): boolean {
    return (this.hasEditAccess && !(this.decision === "Needs removal" || this.decision === "Needs adjustment")) || (!this.hasEditAccess && isNullOrUndefined(this.decisionReason));
  }
}

export class ReviewTasks {
  tasks: Array<ReviewTask>;
  private reviewTasksGroupMap: Map<String, ReviewTasksGroup>;
  private pagination: Pagination;

  constructor(reviewTasks: Array<Object>, pagination: Pagination | null = null) {
    this.tasks = reviewTasks.map((reviewTask) => new ReviewTask(reviewTask));
    if (pagination !== null) {
      this.pagination = pagination;
    }
  }

  private getAssignmentListForBulkDelegation(taskIds: Array<String>): Array<{}> {
    const reviewTasks =  this.tasks.filter((task) => taskIds.includes(task.reviewTaskId));
    const assigneeList = reviewTasks.map(task => task.reassignment.candidates).flat().map((it) => ({ id: it.id, name: it.name }));
    return this.removeDuplicatesByAttribute(assigneeList);
  }

  private removeDuplicatesByAttribute(assigneeList): Array<{}> {
    const uniqueMap = new Map();
    return assigneeList.filter(item => {
      const key = item.id;
      return uniqueMap.has(key) ? false : uniqueMap.set(key, item);
    });
  }


  private filterSavedTasks(tasks: ReviewTask[]): Array<ReviewTask> {
    return tasks.filter((task) => task.isSaved());
  }

  private filterSubmittedTasks(tasks: ReviewTask[]): Array<ReviewTask> {
    return tasks.filter((task) => task.isSubmitted());
  }

  private filterPendingDecisionTasks(tasks: ReviewTask[]): Array<ReviewTask> {
    return tasks.filter((task) => task.isPendingDecision());
  }

  private areAllEditedDecisionsValid(): boolean {
    return !this.tasks.filter((task) => task.isCurrentDecisionDifferentFromOriginal()).some((task) => !task.isValidDecision());
  }

  shouldShowSubmitNotice(): boolean {
    return this.tasks.some((task) => task.hasEditAccess);
  }

  keys(): Array<string> {
    return this.tasks.map((task) => task.key);
  }

  ids(): Array<string> {
    return this.tasks.map(task => task.id);
  }

  areAllTasksNotReAssignable(): boolean {
    return !this.tasks.some((task) => task.reassignment.isReAssignable);
  }

  markTasksThatAreEdited(
    editedReviewTasks: Array<{
      action: string,
      comment: string,
      reviewTaskId: string,
      reviewedBy: string,
      reviewedAt: string,
      decisionReason: Nullable<DecisionReason>
    }>,
    isCommentRequired: Function,
    isActionRequiredWhenCommentIsFilled: Function,
  ) {
    this.tasks.forEach((task) => task.markTaskIfEdited(editedReviewTasks, isCommentRequired, isActionRequiredWhenCommentIsFilled));
  }

  canBeSaved(): boolean {
    return this.tasks.some((task) => task.canBeSaved()) && this.areAllEditedDecisionsValid();
  }

  nonEditedTasks(): Array<string> {
    return this.tasks.filter((task) => !task.isCurrentDecisionDifferentFromOriginal()).map((task) => task.reviewTaskId);
  }

  notMyTasks(): Array<string> {
    return this.tasks.filter((task) => !task.isMyTask).map((task) => task.reviewTaskId);
  }

  editedReviewTasks(): Array<string> {
    return this.tasks.filter((task) => task.isCurrentDecisionDifferentFromOriginal()).map((task) => task.reviewTaskId);
  }

  savePayload(reviewId: string): SaveReviewTasks {
    return new SaveReviewTasks(this.tasks.filter((task) => task.canBeSaved()).map((task) => task.updateTaskPayload()), reviewId);
  }

  submittable(): Array<ReviewTask> {
    return this.tasks.filter((task) => task.canBeSubmitted()); //use set isSelectedForSubmission=true if canBeSubmitted is true instead of canBeSubmitted
  }

  canBeSubmitted(): boolean {
    return this.tasks.some((task) => task.canBeSubmitted()) && this.areAllEditedDecisionsValid();
  }

  editableTasks(): Array<ReviewTask> {
    return this.tasks.filter((task) => task.hasEditAccess);
  }

  hasNoEditableTasks(): boolean {
    return !this.tasks.some((task) => task.hasEditAccess);
  }

  findAllBy(keys: string[]): ReviewTasks {
    return new ReviewTasks(this.tasks.filter((task) => keys.includes(task.key)));
  }

  submitPayload(reviewId: string): SubmitReviewTasks {
    return new SubmitReviewTasks(this.submittable().map((task) => task.updateTaskPayload()), reviewId);
  }

  savedForMyTasksCount(): number {
    return this.tasks.filter((task) => task.isSaved() && task.hasEditAccess).length;
  }

  editableTaskCount(): number {
    return this.tasks.filter((task) => task.hasEditAccess).length;
  }

  markTasksAsIgnored(keys: string[]): ReviewTasks {
    this.tasks.filter(x => keys.includes(x.key)).forEach(x => x.markTaskAsIgnored());
    return this;
  }

  resetIgnoredFlagsToDefault(): ReviewTasks {
    this.tasks.forEach((task) => task.resetIgnoredFlagToFalse());
    return this;
  }

  countOfSubmittableTasks(): number {
    return this.submittable().length;
  }

  submittableTasksNotReviewedBy(accessOwnerName: string) {
    return this.tasks.filter((task) => task.reviewedBy !== accessOwnerName && task.canBeSubmitted());
  }

  submittableTasksReviewedBy(accessOwnerName: string) {
    return this.tasks.filter((task) => task.reviewedBy === accessOwnerName && task.canBeSubmitted());
  }

  submittedTasksReviewedBy(accessOwnerName: string) {
    return this.tasks.filter((task) => task.reviewedBy === accessOwnerName && task.isSubmitted());
  }

  saveDisabledReason(): string | null {
    if (!this.areAllEditedDecisionsValid()) {
      return TooltipMessage.saveForLater.disabled.invalidDecision;
    }
    if (this.editedReviewTasks().length === 0) {
      return TooltipMessage.saveForLater.disabled.noEditedDecisions;
    }
    return null;
  }

  areTasksAssignedToTheCurrentUser(): boolean {
    return this.tasks.some((task) => task.isAssignedToCurrentUser);
  }

  private concatFilteredTasks(filters: Filters): Array<ReviewTask> {
    const tasks = filters[FilterKeys.ShowMyTasks]
      ? this.filterMyTasks(this.tasks)
      : this.tasks;

    if (this.onlyShowMyTasksFilterIsSelected(filters))
      return tasks;

    return (filters[FilterKeys.ShowPendingDecisions] ? this.filterPendingDecisionTasks(tasks) : [])
      .concat(filters[FilterKeys.ShowSavedDecision] ? this.filterSavedTasks(tasks) : [])
      .concat(filters[FilterKeys.ShowSubmittedDecisions] ? this.filterSubmittedTasks(tasks) : []);
  }

  private onlyShowMyTasksFilterIsSelected(filters: Filters) {
    return !filters[FilterKeys.ShowPendingDecisions] && !filters[FilterKeys.ShowSavedDecision] && !filters[FilterKeys.ShowSubmittedDecisions];
  }

  private filterMyTasks(tasks: ReviewTask[]) {
    return tasks.filter((task) => task.isAssignedToCurrentUser);
  }

  viewableTasks(filters: Filters): Array<ReviewTask> {
    return filters[FilterKeys.ShowAll] ? this.tasks : this.concatFilteredTasks(filters);
  }

  viewableDecisionReasons(filters: Filters): Array<number> {
    return this.viewableTasks(filters).filter((task) => !isNullOrUndefined(task.decisionReason)).map((task) => task.decisionReason!!.id);
  }

  push(reviewTask: ReviewTask): ReviewTasks {
    this.tasks.push(reviewTask);
    return this;
  }

  private computeReviewTasksGroupMap() {
    if (this.reviewTasksGroupMap === undefined) {
      this.reviewTasksGroupMap =
        this.tasks.reduce(this.reviewTasksGroupMapReducer(), new Map<String, ReviewTasksGroup>());
    }
  }

  reviewTasksGroup(reviewTaskId: String): ReviewTasksGroup | undefined {
    this.computeReviewTasksGroupMap();
    return this.reviewTasksGroupMap.get(reviewTaskId);
  }

  private createReviewTasksGroup(shouldBeSkipped: boolean): ReviewTasksGroup {
    if (shouldBeSkipped) {
      return {
        rowSpan: 0,
        reviewTaskIds: new Array<String>(),
        shouldBeSkipped: true,
      };
    } else {
      return {
        rowSpan: 1,
        reviewTaskIds: new Array<String>(),
        shouldBeSkipped: false,
      };
    }
  }

  private reviewTaskForSameUser(reviewTask1: ReviewTask, reviewTask2: ReviewTask): boolean {
    return reviewTask1.revieweeId === reviewTask2.revieweeId;
  }

  private isNextReviewTaskInSamePage(
    nextReviewTaskRowIndex: number, reviewTaskRowIndex: number,
  ): boolean {
    const pageNumberForNextReviewTask =
      Math.floor(nextReviewTaskRowIndex / this.pagination.pageSize) + 1;
    const pageNumberForCurrentReviewTask =
      Math.floor(reviewTaskRowIndex / this.pagination.pageSize) + 1;
    return pageNumberForNextReviewTask === pageNumberForCurrentReviewTask;
  }

  private reviewTaskGroupForCurrentReviewTask(
    reviewTaskRowIndex: number,
    reviewTasksArray: Array<ReviewTask>,
    reviewTasksGroup: ReviewTasksGroup,
    currentReviewTask: ReviewTask,
  ): ReviewTasksGroup {
    const outputReviewTasksGroup: ReviewTasksGroup = reviewTasksGroup;
    outputReviewTasksGroup.reviewTaskIds.push(currentReviewTask.reviewTaskId);
    for (let nextReviewTaskRowIndex = reviewTaskRowIndex + 1;
         nextReviewTaskRowIndex < reviewTasksArray.length;
         ++nextReviewTaskRowIndex
    ) {
      const nextReviewTask: ReviewTask = reviewTasksArray[nextReviewTaskRowIndex];
      if (this.reviewTaskForSameUser(nextReviewTask, currentReviewTask)
        && this.isNextReviewTaskInSamePage(nextReviewTaskRowIndex, reviewTaskRowIndex)) {
        outputReviewTasksGroup.rowSpan += 1;
        outputReviewTasksGroup.reviewTaskIds.push(nextReviewTask.reviewTaskId);
      } else {
        break;
      }
    }
    return outputReviewTasksGroup;
  }

  private updateReviewTasksGroupForSubsequentReviewTasks(
    currentReviewTaskIndex: number,
    reviewTasksArray: Array<ReviewTask>,
    reviewTasksGroupMap: Map<String, ReviewTasksGroup>,
    currentReviewTask: ReviewTask) {
    for (let nextReviewTaskIndex = currentReviewTaskIndex + 1;
         nextReviewTaskIndex < reviewTasksArray.length;
         ++nextReviewTaskIndex
    ) {
      const nextReviewTask: ReviewTask = reviewTasksArray[nextReviewTaskIndex];
      if (this.reviewTaskForSameUser(nextReviewTask, currentReviewTask)
        && this.isNextReviewTaskInSamePage(nextReviewTaskIndex, currentReviewTaskIndex)) {
        reviewTasksGroupMap.set(
          nextReviewTask.reviewTaskId,
          this.createReviewTasksGroup(true),
        );
      } else {
        reviewTasksGroupMap.set(
          nextReviewTask.reviewTaskId,
          this.createReviewTasksGroup(false),
        );
        break;
      }
    }
  }

  private reviewTasksGroupMapReducer() {
    return (
      reviewTasksGroupMap: Map<String, ReviewTasksGroup>,
      currentReviewTask: ReviewTask,
      currentReviewTaskIndex: number,
      reviewTasksArray: Array<ReviewTask>) => {
      const reviewTasksGroup =
        reviewTasksGroupMap.get(currentReviewTask.reviewTaskId) ??
        this.createReviewTasksGroup(false);

      if (reviewTasksGroup.shouldBeSkipped) return reviewTasksGroupMap;

      // At this point computation of reviewTasksGroup for currentReviewTask should NOT be skipped.
      // So reviewTasksGroup of currentReviewTask must already have been initialized
      //  with valid defined properties.
      this.updateReviewTasksGroupForSubsequentReviewTasks(
        currentReviewTaskIndex, reviewTasksArray, reviewTasksGroupMap, currentReviewTask,
      );
      reviewTasksGroupMap.set(
        currentReviewTask.reviewTaskId,
        this.reviewTaskGroupForCurrentReviewTask(
          currentReviewTaskIndex, reviewTasksArray, reviewTasksGroup, currentReviewTask,
        ),
      );
      return reviewTasksGroupMap;
    };
  }

  totalEditableTasksForReviewee(userId: String): EditableReviewTasksForAReviewee {
    return new EditableReviewTasksForAReviewee(this.tasks.filter((task) => task.revieweeId === userId && task.hasEditAccess));
  }

  filterReAssignableTasks(): ReAssignableReviewTasks {
    return new ReAssignableReviewTasks(this.tasks.filter((it) => it.reassignment.isReAssignable));
  }

  filterNotDelegatedTasksForReviewer(reviewerName: string): ReviewTasks {
    return new ReviewTasks(this.tasks.filter((it) => !it.delegatedTo.split(", ").includes(reviewerName)));
  }
}

export class EditableReviewTasksForAReviewee extends ReviewTasks {
  isUserTaskGroupCheckBoxSelected(selectedRowKeys: string[]): boolean {
    return this.tasks.length > 0
      && difference(this.tasks.map((it) => it.key), selectedRowKeys).length === 0;
  }
}

export class ReAssignableReviewTasks extends ReviewTasks {
  tooltipMessage(): string {
    if (this.tasks.length < 1) {
      return "Not assignable";
    } else if (this.isNotDelegated()) {
      return this.tasks[0].reassignment.tooltipMessage;
    } else {
      return this.tasks.find((it) => !it.reassignment.hasNoDelegations)!!.reassignment.tooltipMessage;
    }
  }

  isReassignmentEnabled(): boolean {
    return this.tasks.length > 0;
  }

  isNotDelegated(): boolean {
    return this.tasks.every((it) => it.reassignment.hasNoDelegations);
  }
}